
import React, { useState } from 'react';
import { Building, Map, Briefcase, Check, BarChart } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { Badge } from '@/components/ui/badge';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';

const AcquisitionFocusSection = () => {
  const [activeTab, setActiveTab] = useState('business');
  const isMobile = useIsMobile();
  const location = useLocation();
  const { language, t } = useLanguage();
  const isWhySellPage = location.pathname.includes('why-sell-to-noa');

  const focusCategories = [
    {
      id: 'business',
      title: language === 'es' ? 'Perfil de Negocio' : 'Business Profile',
      icon: Briefcase,
      description: language === 'es' 
        ? 'Buscamos empresas de software B2B que ofrecen soluciones de misión crítica en mercados de nicho.' 
        : 'We seek B2B software companies delivering mission-critical solutions in niche markets.',
      bullets: language === 'es' ? [
        'Empresas de software B2B con modelos de negocio comprobados',
        'Operando en mercados de nicho especializados',
        'Soluciones de misión crítica en las que los clientes confían diariamente'
      ] : [
        'B2B software companies with proven business models',
        'Operating in specialized niche markets',
        'Mission-critical solutions that customers rely on daily'
      ]
    },
    {
      id: 'metrics',
      title: language === 'es' ? 'Métricas de la Empresa' : 'Company Metrics',
      icon: BarChart,
      description: language === 'es'
        ? 'Buscamos empresas establecidas con fundamentos sólidos y modelos de negocio sostenibles.'
        : 'We look for established companies with strong fundamentals and sustainable business models.',
      bullets: language === 'es' ? [
        'Ingresos entre $1-10M anuales con crecimiento histórico constante',
        'Altas tasas de retención de clientes (renovación anual del 80%+)',
        'Márgenes de beneficio saludables o un camino claro hacia la rentabilidad'
      ] : [
        'Revenue between $1-10M annually with consistent historical growth',
        'High customer retention rates (80%+ annual renewal)',
        'Healthy profit margins or clear path to profitability'
      ]
    },
    {
      id: 'transaction',
      title: language === 'es' ? 'Tipo de Transacción' : 'Transaction Type',
      icon: Building,
      description: language === 'es'
        ? 'Priorizamos estructuras de acuerdos simples y flexibles que se alineen con tus objetivos y planes futuros.'
        : 'We prioritize simple, flexible deal structures that align with your goals and future plans.',
      bullets: language === 'es' ? [
        'Salida Completa – Vende el 100% de tu negocio y decide si quieres retirarte, seguir involucrado o asumir un rol de asesor.',
        'Salida Parcial – Vende más del 70% de tu negocio mientras continúas dirigiéndolo con nuestro apoyo estratégico.'
      ] : [
        'Complete Exit – Sell 100% of your business and decide whether to retire, stay involved, or take on an advisory role.',
        'Partial Exit – Sell over 70% of your business while continuing to run it with our strategic support.'
      ]
    }
  ];

  return (
    <section className={cn(
      "py-24",
      isWhySellPage 
        ? "bg-white dark:bg-noa-rich-black" 
        : "bg-gradient-to-br from-noa-antiflash-white to-[#F8FAFC] dark:from-noa-rich-black dark:to-noa-rich-black/95"
    )}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="text-left mb-16">
          <h2 className="font-space-grotesk text-5xl sm:text-6xl font-bold mb-6 text-[#0D1F2D] dark:text-noa-antiflash-white">
            {language === 'es' ? 'Enfoque de' : 'Acquisitions'}
            <div className="flex items-center">
              <span className="text-noa-rich-black dark:text-noa-antiflash-white">
                {language === 'es' ? 'adquisiciones' : 'Focus'}<span className="text-blue-600">.</span>
              </span>
            </div>
          </h2>
          <p className="text-noa-charcoal/80 dark:text-noa-antiflash-white/70 text-lg max-w-3xl">
            {language === 'es' 
              ? 'Buscamos empresas de software B2B que ofrecen soluciones de misión crítica en mercados de nicho.'
              : 'We seek B2B software companies delivering mission-critical solutions in niche markets.'}
          </p>
        </div>

        {isMobile ? (
          <div className="space-y-8">
            {focusCategories.map((category) => (
              <div key={category.id} className="bg-[#F0F5FF] dark:bg-blue-900/10 rounded-lg p-8">
                <div className="flex items-center mb-6">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30 mr-5">
                    <category.icon className="w-7 h-7 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-space-grotesk text-2xl font-bold text-noa-rich-black dark:text-noa-antiflash-white">
                      {category.title}
                    </h3>
                  </div>
                </div>

                <p className="text-lg text-noa-charcoal/90 dark:text-noa-antiflash-white/80 mb-6">
                  {category.description}
                </p>

                <div className="space-y-4">
                  {category.bullets.map((bullet, idx) => (
                    <div key={idx} className="flex items-center">
                      <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mr-3">
                        <Check className="w-4 h-4 text-blue-600" />
                      </div>
                      <span className="text-noa-rich-black dark:text-noa-antiflash-white">
                        {bullet}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row">
            <div className="lg:w-1/3 mb-8 lg:mb-0 lg:pr-8">
              <div className="flex flex-col space-y-4">
                {focusCategories.map((category) => (
                  <div 
                    key={category.id}
                    onClick={() => setActiveTab(category.id)}
                    className={cn(
                      "p-6 rounded-lg cursor-pointer transition-all duration-300",
                      activeTab === category.id 
                        ? "bg-[#F0F5FF] dark:bg-blue-900/20 border-l-4 border-blue-600" 
                        : "bg-white/50 dark:bg-noa-rich-black/50 hover:bg-[#F8FAFC] dark:hover:bg-noa-rich-black/70"
                    )}
                  >
                    <div className="flex items-start">
                      <div>
                        <h3 className="font-space-grotesk text-2xl font-bold text-noa-rich-black dark:text-noa-antiflash-white">
                          {category.title}
                        </h3>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:w-2/3 bg-[#F0F5FF] dark:bg-blue-900/10 rounded-lg p-8 md:p-12">
              {focusCategories.map((category) => (
                <div 
                  key={category.id} 
                  className={cn(
                    "transition-opacity duration-300",
                    activeTab === category.id ? "block" : "hidden"
                  )}
                >
                  <div className="flex items-center mb-6">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30 mr-6">
                      <category.icon className="w-8 h-8 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-space-grotesk text-3xl md:text-4xl font-bold text-noa-rich-black dark:text-noa-antiflash-white">
                        {category.title}
                      </h3>
                    </div>
                  </div>

                  <p className="text-lg text-noa-charcoal/90 dark:text-noa-antiflash-white/80 mb-8">
                    {category.description}
                  </p>

                  <div className="space-y-4">
                    {category.bullets.map((bullet, idx) => (
                      <div key={idx} className="flex items-center">
                        <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mr-3">
                          <Check className="w-4 h-4 text-blue-600" />
                        </div>
                        <span className="text-noa-rich-black dark:text-noa-antiflash-white">
                          {bullet}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default AcquisitionFocusSection;
